"use client";

import { useState } from "react";
import { X, AlertTriangle, CalendarClock, Info } from "lucide-react";
import { usePricingModal } from "../hooks/usePricingModal";
import { useUser } from "../hooks/useUser";
import {
  useSubscriptionNotice,
  type SubscriptionNotice,
} from "../hooks/useSubscriptionNotice";
import { useBilling } from "@/features/billing/hooks/useBilling";
import { PLAN_CONFIG, isPaidPlanId } from "@/lib/billing/planConfig";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function endingTitle(label: string, daysLeft: number): string {
  if (daysLeft <= 1) return `${label} ends today`;
  if (daysLeft === 2) return `${label} ends tomorrow`;
  return `${label} ends in ${daysLeft} days`;
}

/**
 * Плаваюча картка знизу-зліва (низ-право зайняте CompareFloatingPanel +
 * ScrollNavButtons). Рендериться ТІЛЬКИ у 3 станах підписки (past_due /
 * догорає ≤3д / щойно закінчилась) — активний Pro з робочою карткою її
 * ніколи не бачить. Dismiss-логіка в useSubscriptionNotice.
 */
export function SubscriptionNoticeBanner() {
  const { notice, dismiss } = useSubscriptionNotice();
  const { open: openPricing } = usePricingModal();
  const { plan } = useUser();
  const billing = useBilling();
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  if (!notice) return null;

  async function runAction(fn: () => Promise<{ ok: boolean; message?: string }>) {
    setActionLoading(true);
    setActionError(null);
    const r = await fn();
    setActionLoading(false);
    if (!r.ok) setActionError(r.message ?? "Something went wrong");
  }

  // The plan a notice is about: the current paid tier, or — once it has
  // ended and the row reads free — no name at all rather than a wrong one.
  const planLabel = isPaidPlanId(plan) ? PLAN_CONFIG[plan].label : null;

  const { icon, accent, title, body, actionLabel, onAction } =
    describeNotice(
      notice,
      planLabel,
      plan === "studio",
      // Same-tab portal (see useBilling): a popup after an await is blocked on Safari/iOS.
      () => runAction(billing.openPortal),
      () => runAction(billing.resume),
      () => openPricing("subscription_notice"),
    );

  return (
    <div
      // right-20 на мобілці лишає прохід до ScrollNavButtons (bottom-5 right-5
      // w-11); z-[60] — вище за CompareFloatingPanel/ScrollNavButtons (z-50),
      // щоб білінговий нотис не ховався під ними, але нижче модалок (z-[100]).
      className="fixed bottom-4 left-4 right-20 sm:right-auto sm:max-w-[340px] z-[60] rounded-lg border border-[#2a475e] bg-[#16202d] shadow-2xl overflow-hidden"
      role="status"
    >
      <div className="flex items-start gap-2.5 p-3">
        <span className="mt-0.5 shrink-0" style={{ color: accent }}>
          {icon}
        </span>
        <div className="min-w-0 flex-1 space-y-1.5">
          <p className="text-sm font-medium text-white leading-tight">{title}</p>
          <p className="text-xs text-[#8f98a0] leading-snug">{body}</p>
          <div className="pt-1 flex items-center gap-2">
            <button
              type="button"
              disabled={actionLoading}
              onClick={onAction}
              className="px-2.5 py-1 rounded-md text-xs font-medium text-white bg-gradient-to-r from-[#66c0f4] to-[#4c9cd5] hover:from-[#76caf7] hover:to-[#5aa6df] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {actionLoading ? "Loading..." : actionLabel}
            </button>
            {actionError && (
              <span className="text-[11px] text-red-400">{actionError}</span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="shrink-0 text-[#8f98a0] hover:text-white transition-colors p-0.5 -m-0.5"
        >
          <X size={14} />
        </button>
      </div>
      <div className="h-0.5" style={{ backgroundColor: accent }} />
    </div>
  );
}

function describeNotice(
  notice: SubscriptionNotice,
  planLabel: string | null,
  /** A Studio subscriber loses working API keys with the plan — worth saying. */
  hasApiKeys: boolean,
  openBillingPortal: () => void,
  resume: () => void,
  openPricing: () => void,
): {
  icon: React.ReactNode;
  accent: string;
  title: string;
  body: string;
  actionLabel: string;
  onAction: () => void;
} {
  switch (notice.kind) {
    case "past_due":
      return {
        icon: <AlertTriangle size={16} />,
        accent: "#e35e1c",
        title: "Payment issue",
        body: planLabel
          ? `We couldn't renew your ${planLabel} subscription. Update your payment method to keep ${planLabel}.`
          : "We couldn't renew your subscription. Update your payment method to get your plan back.",
        actionLabel: "Update payment method",
        onAction: openBillingPortal,
      };
    case "ending":
      return {
        icon: <CalendarClock size={16} />,
        accent: "#66c0f4",
        title: endingTitle(planLabel ?? "Your plan", notice.daysLeft),
        body: `Your subscription is set to cancel on ${formatDate(notice.endsAt)}.${hasApiKeys ? " Your API keys and the MCP server stop working then." : ""} Resume it before then to keep everything.`,
        actionLabel: "Resume subscription",
        onAction: resume,
      };
    case "expired":
      return {
        icon: <Info size={16} />,
        accent: "#a4d007",
        title: "Your subscription ended",
        body: `Your paid plan ended on ${formatDate(notice.endedAt)}. Chart history is back to 3 months and paid features are locked.`,
        actionLabel: "See plans",
        onAction: openPricing,
      };
  }
}
